import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { BillingTier } from '../lib/billing';
import type { WorkspaceTrial } from '../lib/trial';
import { useAuthStore } from '../store/auth';
import { useWorkspace } from './useWorkspace';

export type ResponsesQuotaState = 'ok' | 'warning' | 'exceeded';
export type WhiteLabelLevel = 'NONE' | 'BASIC' | 'FULL';

export interface WorkspaceUsage {
  tier: BillingTier;
  planName: string;
  responsesUsed: number;
  responsesLimit: number | null;
  aiGenerationsUsed: number;
  aiGenerationsLimit: number | null;
  surveysLimit: number | null;
  collaboratorsLimit?: number | null;
  workspaceLimit?: number | null;
  sdkEnabled?: boolean;
  apiKeysEnabled?: boolean;
  webhooksEnabled?: boolean;
  whiteLabelLevel?: WhiteLabelLevel;
  trial?: WorkspaceTrial | null;
  periodStart: string;
  periodEnd: string;
  /** Newer APIs report it; older ones are derived from used/limit (see responsesQuotaState). */
  responsesQuotaState?: ResponsesQuotaState;
}

/** The share of the monthly response quota at which the dashboard starts warning. */
export const RESPONSES_WARNING_RATIO = 0.8;

export function responsesQuotaState(usage: WorkspaceUsage | null | undefined): ResponsesQuotaState {
  if (!usage) return 'ok';
  if (usage.responsesQuotaState) return usage.responsesQuotaState;
  const limit = usage.responsesLimit;
  if (!limit || limit <= 0) return 'ok';
  if (usage.responsesUsed >= limit) return 'exceeded';
  return usage.responsesUsed >= limit * RESPONSES_WARNING_RATIO ? 'warning' : 'ok';
}

export function workspaceUsageQueryKey(workspaceId: string | null | undefined) {
  return ['workspace-usage', workspaceId ?? ''] as const;
}

/** Current-period usage and entitlements of a workspace; one shared query for the whole app. */
export function useWorkspaceUsage(workspaceIdOverride?: string | null) {
  const { workspaceId: activeWorkspaceId } = useWorkspace();
  const workspaceId = workspaceIdOverride === undefined ? activeWorkspaceId : workspaceIdOverride;
  const isRespondentEmbed = useAuthStore((s) => s.isEmbedded && s.embedContext?.role === 'respondent');
  return useQuery({
    queryKey: workspaceUsageQueryKey(workspaceId),
    queryFn: async () => {
      const res = await api.get<{ usage: WorkspaceUsage }>(`/workspaces/${workspaceId}/usage`);
      return res.data.usage;
    },
    enabled: !!workspaceId && !isRespondentEmbed,
    staleTime: 30_000,
  });
}

export interface Entitlements {
  tier: BillingTier;
  sdk: boolean;
  apiKeys: boolean;
  webhooks: boolean;
  /** White-label: hiding ResponseDock branding and a brand name (Pro). Logo and colours work on every plan. */
  branding: boolean;
  /** Custom domain (Business). */
  customDomain: boolean;
  known: boolean;
}

/**
 * What the workspace may use right now. Unknown (still loading, or an older API) counts as allowed,
 * so lock badges never appear on features the workspace might have.
 */
export function entitlementsFromUsage(usage: WorkspaceUsage | null | undefined): Entitlements {
  if (!usage) {
    return { tier: 'STARTER', sdk: true, apiKeys: true, webhooks: true, branding: true, customDomain: true, known: false };
  }
  const level = usage.whiteLabelLevel;
  return {
    tier: usage.tier,
    sdk: usage.sdkEnabled ?? true,
    apiKeys: usage.apiKeysEnabled ?? true,
    webhooks: usage.webhooksEnabled ?? true,
    branding: level ? level !== 'NONE' : true,
    customDomain: level ? level === 'FULL' : true,
    known: true,
  };
}

export function useEntitlements() {
  const usage = useWorkspaceUsage();
  return { ...entitlementsFromUsage(usage.data), usage: usage.data ?? null, isLoading: usage.isLoading };
}
